/**
 * Service for fetching tabular data for the Data Table view.
 * Uses a single POST /retrieve-all call with server-side filtering,
 * pagination, and sorting handled by DuckDB in the Lambda.
 */

import { type LayerConfig, type FieldSchema } from '../../../types/metadata';
import { type ColumnFilter } from '../utils/tableFilterUtils';
import { getValidAuthHeaders } from '../../../utils/authHeaders';

const API_BASE_URL = process.env.REACT_APP_API_URL || '';

export interface TableDataRequest {
  layer: LayerConfig;
  fields?: string[];
  filters?: ColumnFilter[];
  offset?: number;
  limit?: number;
  sortField?: string;
  sortDirection?: 'asc' | 'desc';
  viewportBounds?: {
    north: number;
    south: number;
    east: number;
    west: number;
  };
}

export interface TableDataResponse {
  data: Record<string, any>[];
  totalCount: number;
  offset: number;
  limit: number;
  hasMore: boolean;
}

/**
 * Convert ColumnFilter[] to the API's filter format.
 * API format: { "fieldName": "operator value" }
 * e.g. { "age": ">30", "city": "Seattle" }
 * 
 * For = operator, just the value.
 * For IN, comma-separated values prefixed with "IN ".
 * For others, "operator value" (e.g. ">30", "!=foo").
 * 
 * Multiple filters on the same field are passed as an array.
 */
export const buildApiFilters = (filters: ColumnFilter[]): Record<string, string | string[]> => {
  const apiFilters: Record<string, string | string[]> = {};

  for (const filter of filters) {
    let filterStr: string;
    
    switch (filter.operator) {
      case '=':
        filterStr = filter.value;
        break;
      case 'IN':
        filterStr = `IN ${filter.value}`;
        break;
      default:
        filterStr = `${filter.operator}${filter.value}`;
        break;
    }

    if (apiFilters[filter.field]) {
      // Multiple filters on same field — convert to array
      const existing = apiFilters[filter.field];
      if (Array.isArray(existing)) {
        existing.push(filterStr);
      } else {
        apiFilters[filter.field] = [existing, filterStr];
      }
    } else {
      apiFilters[filter.field] = filterStr;
    }
  }

  return apiFilters;
};

/**
 * Fetch tabular data for a layer with server-side pagination, filtering, and sorting.
 * Uses a single POST to /retrieve-all which handles everything in one Lambda invocation
 * with parallel S3 downloads and efficient DuckDB queries.
 */
export const fetchTableData = async (
  request: TableDataRequest
): Promise<TableDataResponse> => {
  const {
    layer,
    fields,
    filters = [],
    offset = 0,
    limit = 25,
    sortField,
    sortDirection = 'asc',
    viewportBounds,
  } = request;

  const requestFields = fields || getTableFields(layer.schema);
  const apiFilters = filters.length > 0 ? buildApiFilters(filters) : undefined;

  const requestBody: Record<string, any> = {
    fileId: layer.id,
    version: layer.version,
    fields: requestFields,
    limit,
    offset,
  };

  if (apiFilters) {
    requestBody.filters = apiFilters;
  }

  if (sortField) {
    requestBody.sortField = sortField;
    requestBody.sortDirection = sortDirection;
  }

  if (viewportBounds) {
    requestBody.viewportBounds = viewportBounds;
  }

  const response = await fetch(`${API_BASE_URL}/retrieve-all`, {
    method: 'POST',
    headers: await getValidAuthHeaders(),
    body: JSON.stringify(requestBody),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.error || `Failed to fetch data: ${response.status}`);
  }

  const result = await response.json();

  return {
    data: result.data || [],
    totalCount: result.totalCount || 0,
    offset: result.offset ?? offset,
    limit: result.limit ?? limit,
    hasMore: result.hasMore ?? false,
  };
};

/**
 * Get displayable table fields from schema (exclude geometry fields)
 */
export const getTableFields = (schema: FieldSchema): string[] => {
  return Object.entries(schema)
    .filter(([, schemaEntry]) => {
      const type = typeof schemaEntry === 'object' ? schemaEntry.type : schemaEntry;
      return type !== 'geometry';
    })
    .map(([fieldName]) => fieldName);
};
